import { can, PortalPermissions } from "../../../../../config/permissions";
import {LiveTableProps} from "../../../types";
import useGetMe from "../../../../../hooks/useCurrentUser";
import {useEffect, useState} from "react";
import {formatDateView} from "../../../../adiqEdi/detalhesEdi/utils";
import LoadingOverlay from "../../../../../components/LoadingOverlay";
import {Button, TableContainer} from "../../../styles";
import Table, {TableColumn} from "../../../../../components/Table";
import {
  formatDivisionRuleView,
  formatEffectContractStatusView,
  formatMoneyView,
} from "../../../../../utils/formatViews";
import {EyeOutlined} from "@ant-design/icons";
import ContractEffectsService from "../../contractEffectsService";
import {ContractEffectBankRecords, ContractEffectHistoryRecords,} from "../modals/types";
import {getErrorMessage} from "../../../../../utils/getErrorMessage";
import {BankModal} from "../modals/bankModal";
import {HistoryModal} from "../modals/historyModal";
import {Checkbox} from "antd";
import {toast} from "react-toastify";

export const LiveTable: React.FC<LiveTableProps> = (
  {
    data,
    currentPage,
    pageSize,
    onPageChange,
    onPageSizeChange,
    filterData
  }) => {
  const user = useGetMe();
  const contractEffectsService = new ContractEffectsService();
  const formattedDate = (date: string) => formatDateView(date);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bankModalOpen, setBankModalOpen] = useState<boolean>(false);
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false);
  const [bankContractData, setBankContractData] =
    useState<ContractEffectBankRecords>({
      hash: "",
      records: [],
    });
  const [historyContractData, setHistoryContractData] =
    useState<ContractEffectHistoryRecords>({
      hash: "",
      records: [],
    });
  const [payedUr, setPayedUr] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [allUnpaidHashes, setAllUnpaidHashes] = useState<string[]>([]);
  const [paidHashes, setPaidHashes] = useState<string[]>([]);
  const [statusBHashes, setStatusBHashes] = useState<string[]>([]);
  const [otherStatusHashes, setOtherStatusHashes] = useState<string[]>([]);
  const [newlySelectedHashes, setNewlySelectedHashes] = useState<string[]>([]);

  const handleOpenBankModal = async (hashIdentifier: string) => {
    try {
      setIsLoading(true);
      setBankModalOpen(true);
      const response = await contractEffectsService.getContractEffectPayment(
        hashIdentifier
      );

      if (response.length > 0)
        setBankContractData({
          hash: hashIdentifier,
          records: response,
        });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseBankModal = () => {
    setBankModalOpen(false);
    setBankContractData({
      hash: "",
      records: [],
    });
  };

  const handleOpenHistoryModal = async (hashIdentifier: string) => {
    try {
      setIsLoading(true);
      setHistoryModalOpen(true);
      const response = await contractEffectsService.getContractEffectHistory(
        hashIdentifier
      );

      if (response.length > 0)
        setHistoryContractData({
          hash: hashIdentifier,
          records: response,
        });
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.log(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseHistoryModal = () => {
    setHistoryModalOpen(false);
    setBankContractData({
      hash: "",
      records: [],
    });
  };

  const handleHeaderCheckboxChange = (event: any) => {
    if (event.target.checked) {
      const allSelected = [...statusBHashes, ...otherStatusHashes];
      setSelectedRows(allSelected);
      setNewlySelectedHashes(otherStatusHashes);
    } else {
      setSelectedRows(statusBHashes);
      setNewlySelectedHashes([]);
    }
  };

  const isHeaderCheckboxChecked = () => {
    return (
      selectedRows.length === statusBHashes.length + otherStatusHashes.length
    );
  };

  useEffect(() => {
    const statusBHashes = data.records
      .filter((record) => record.status === "B" && record.hashIdentifier)
      .map((record) => record.hashIdentifier);

    const otherStatusHashes = data.records
      .filter((record) => record.status !== "B" && record.hashIdentifier)
      .map((record) => record.hashIdentifier);

    setStatusBHashes(statusBHashes);
    setOtherStatusHashes(otherStatusHashes);
    setSelectedRows(statusBHashes);
    setNewlySelectedHashes([]);
  }, [data.records]);

  const isHeaderCheckboxIndeterminate = () => {
    const selectedOtherStatusCount = selectedRows.filter((hash) =>
      otherStatusHashes.includes(hash)
    ).length;

    return (
      selectedOtherStatusCount > 0 &&
      selectedOtherStatusCount < otherStatusHashes.length
    );
  };

  const handleSaveClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    try {
      setIsLoading(true);

      if (!isDateRangeSingleDay(filterData?.initialDate, filterData?.finalDate)) {
        toast.error("Não é possível dar baixa em mais de uma dia por vez. Por favor, selecione um único dia.");
      }
      else {

        if (newlySelectedHashes.length > 0) {
          await contractEffectsService.markAsPaid(newlySelectedHashes, filterData?.initialDate as string);

          setStatusBHashes((prev) => [...prev, ...newlySelectedHashes]);
          setOtherStatusHashes((prev) =>
            prev.filter((hash) => !newlySelectedHashes.includes(hash))
          );

          setNewlySelectedHashes([]);

          window.location.reload();
        }
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      console.error("Erro ao salvar:", errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getPendingChangesCount = () => {
    return newlySelectedHashes.length;
  };

  const isRowSelected = (hashIdentifier: string) => {
    return selectedRows.includes(hashIdentifier);
  };

  const isRowDisabled = (status: string) => {
    return status === "B";
  };

  const isDateRangeSingleDay = (initialDate: string | undefined, finalDate: string | undefined) => {
    if (initialDate === undefined || finalDate === undefined) return false;

    let auxInitialDate = new Date(initialDate).setHours(0, 0, 0, 0);
    let auxFinalDate = new Date(finalDate).setHours(0, 0, 0, 0);

    return auxInitialDate === auxFinalDate;
  }

  return (
    <div>
      <LoadingOverlay show={isLoading} relative/>

      <TableContainer>
        {user.userType === 3 && (
          <div style={{marginBottom: 16, textAlign: "right"}}>
            {can(PortalPermissions.contratos.efeitosCumpridos.darBaixa) && (
              <Button
                type="primary"
                onClick={handleSaveClick}
                disabled={newlySelectedHashes.length === 0}
              >
                Salvar
              </Button>
            )}
            {newlySelectedHashes.length > 0 && (
              <span style={{marginLeft: 8}}>
                {newlySelectedHashes.length}
                alteração(ões) pendente(s)
              </span>
            )}
          </div>
        )}
        <Table
          dataSource={data.records}
          pagination={{
            defaultPageSize: pageSize,
            defaultCurrent: currentPage,
            current: currentPage,
            onShowSizeChange: (_, size) => {
              onPageSizeChange(size);
            },
            total: data.count,
            onChange: (pageNum) => onPageChange(pageNum),
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} efeitos`,
            showSizeChanger: true,
          }}
        >
          <TableColumn
            title="Código do Lojista"
            dataIndex="merchantId"
            key="merchantId"
          />
          <TableColumn
            title="Usuario final recebedor"
            dataIndex="finalReceiverUser"
            key="finalReceiverUser"
          />
          <TableColumn
            title="Razão Social"
            dataIndex="businessName"
            key="businessName"
          />
          <TableColumn
            title="Data de liquidação"
            dataIndex="settlementDate"
            key="settlementDate"
            render={formatDateView}
          />
          <TableColumn
            title="Tipo de efeito"
            dataIndex="contractEffectType"
            key="contractEffectType"
          />
          <TableColumn
            title="Valor da UR"
            dataIndex="urValue"
            key="urValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Valor Transferido Credor"
            dataIndex="creditorTransferredValue"
            key="creditorTransferredValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Valor Transferido Cliente"
            dataIndex="clientTransferredValue"
            key="clientTransferredValue"
            render={formatMoneyView}
          />
          <TableColumn
            title="Arranjo"
            dataIndex="paymentArrangement"
            key="paymentArrangement"
          />
          <TableColumn
            title="Referência Externa"
            dataIndex="externalReference"
            key="externalReference"
          />
          <TableColumn
            title="Status"
            dataIndex="status"
            key="status"
            render={formatEffectContractStatusView}
          />
          <TableColumn
            title="Detalhes Pagamentos do Efeito"
            dataIndex="visualizar"
            key="visualizar"
            render={(_: any, transaction: any) =>
              transaction.hashIdentifier ? (
                <EyeOutlined
                  style={{
                    fontSize: "20px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                  onClick={() =>
                    handleOpenBankModal(transaction.hashIdentifier)
                  }
                />
              ) : null
            }
          />

          {user.userType === 3 && (
            <TableColumn
              title="Rastreio do Efeito"
              dataIndex="visualizar"
              key="visualizar"
              render={(_: any, transaction: any) =>
                transaction.hashIdentifier ? (
                  <EyeOutlined
                    style={{
                      fontSize: "20px",
                      display: "flex",
                      justifyContent: "center",
                    }}
                    onClick={() =>
                      handleOpenHistoryModal(transaction.hashIdentifier)
                    }
                  />
                ) : null
              }
            />
          )}

          {user.userType === 3 && (
            <TableColumn
              title={
                <Checkbox
                  checked={isHeaderCheckboxChecked()}
                  indeterminate={isHeaderCheckboxIndeterminate()}
                  onChange={handleHeaderCheckboxChange}
                >
                  Pago
                </Checkbox>
              }
              dataIndex="status"
              key="status"
              render={(status: string, record: any) => (
                <Checkbox
                  checked={isRowSelected(record.hashIdentifier)}
                  disabled={isRowDisabled(status)}
                  style={{
                    pointerEvents: isRowDisabled(status) ? "none" : "auto",
                    cursor: isRowDisabled(status) ? "not-allowed" : "pointer",
                  }}
                />
              )}
            />
          )}
        </Table>
      </TableContainer>

      <BankModal
        isShowing={bankModalOpen}
        modalData={bankContractData}
        onClose={handleCloseBankModal}
      />

      <HistoryModal
        isShowing={historyModalOpen}
        modalData={historyContractData}
        onClose={handleCloseHistoryModal}
      />
    </div>
  );
};

export default LiveTable;
